import { patchState, signalStore, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { FilterService, FiltersMap } from 'src/app/content/_components/filter/filter.service';
import {
  ColumnFilterAction,
  ColumnFilterApplied,
  ColumnFilterChange,
  ColumnFilterComponent,
} from 'src/app/content/_components/filter/column-filter/column-filter.component';
import { getTypeIconCombination, getUniqueValues, Row, Table } from 'src/app/models/ui/table';
import { type RxMethod, rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { effect, inject, InjectionToken, Provider, Signal, Type, DOCUMENT } from '@angular/core';

export function provideFilterStore({
  storage,
  secondStore,
}: { storage?: string; secondStore?: boolean } = {}): Provider[] {
  return [
    { provide: FILTER_STORAGE_CONFIG, useValue: ensureConfig(storage) },
    { provide: FILTER_TABLE_STORE, useFactory: createStore },
    secondStore ? { provide: FILTER_TABLE_STORE_II, useFactory: createStore } : [],
  ];
}

export const FILTER_TABLE_STORE = new InjectionToken<FilterStore>('FilterStore');
export const FILTER_TABLE_STORE_II = new InjectionToken<FilterStore>('FilterStore_II');

export interface FilterStore<T extends Row = Row> {
  uniqueValues: Signal<Record<string, Set<unknown>>>;
  iconMap: Signal<Map<string, number>>;
  filteredRows: Signal<T[]>;
  registerTable: RxMethod<Table<T>>;
  registerFilters: RxMethod<readonly ColumnFilterComponent[]>;
  deleteAllFilters(): void;
  onChange(columnFilterChange: ColumnFilterChange): void;
  onRedirect(cb: (_: ColumnFilterApplied) => void): void;
  setFilters(filters: FiltersMap): void;
}

const FilterTableStore = signalStore(
  withState({
    _table: null as Table,
    _filters: [] as readonly ColumnFilterComponent[],
    uniqueValues: {} as Record<string, Set<unknown>>,
    iconMap: {} as Map<string, number>,
    filteredRows: [] as Row[],
  }),

  withProps(() => ({
    ...inject(FILTER_STORAGE_CONFIG, { self: true }),
    _filterService: inject<FilterService>(FilterService, { optional: true }) ?? new FilterService(),
  })),
  withMethods(store => ({
    registerTable: rxMethod(
      tapResponse({
        next: (table: Table) => {
          if (!table) return;
          store._filterService.originalData = [...table.rows];
          patchState(store, {
            _table: { ...table },
            uniqueValues: getUniqueValues(table),
            iconMap: getTypeIconCombination(table),
            filteredRows: store._filterService.filteredData,
          });
        },
        error: console.error,
      })
    ),

    registerFilters: rxMethod(
      tapResponse({
        next: (filters: readonly ColumnFilterComponent[]) => {
          patchState(store, { _filters: filters });
        },
        error: console.error,
      })
    ),

    deleteAllFilters() {
      store._filterService.clearFilters();
      if (store._withStoring) sessionStorage.removeItem(store._storageName);
      store._filters()?.forEach(filterColumn => filterColumn.resetItems());
      patchState(store, { filteredRows: store._table().rows });
    },

    onChange(columnFilterChange: ColumnFilterChange) {
      patchState(store, { filteredRows: store._filterService.filter(columnFilterChange) });
    },

    onRedirect(cb: (_: ColumnFilterApplied) => void) {
      for (const filter of Object.values(store._filterService.filters)) {
        if (filter.reason == ColumnFilterAction.Redirect) cb(filter);
      }
    },

    setFilters(filters: FiltersMap) {
      store._filterService.filters = filters;
      patchState(store, { filteredRows: store._filterService.filteredData, _table: { ...store._table() } });
    },

    _saveInStorage() {
      const changes = JSON.stringify(store._filterService.filters);
      if (changes != '{}') sessionStorage.setItem(store._storageName, changes);
    },
  })),
  withHooks({
    onInit(state) {
      effect(() => {
        if (!state._table() || !state._filters().length || !state._filterService.isAktiv) return;

        setTimeout(() => {
          if (state._withStoring) sessionStorage.removeItem(state._storageName);

          for (const filterApplied of Object.values(state._filterService.filters)) {
            const columnFilter = state._filters().find(filter => filter.column().id == filterApplied.columnId);

            if (columnFilter) columnFilter.restoreFilter(filterApplied);
            else state._filterService.filter({ reason: ColumnFilterAction.Reset, columnId: filterApplied.columnId });
          }
          patchState(state, { filteredRows: state._filterService.filteredData });
        }, 100);
      });

      if (!state._withStoring) return;

      inject(DOCUMENT).defaultView.onbeforeunload = state._saveInStorage;

      const stored: FiltersMap = JSON.parse(sessionStorage.getItem(state._storageName));
      state._filterService.filters = stored;
    },
    onDestroy(state) {
      if (state._withStoring) state._saveInStorage();
    },
  })
) satisfies Type<FilterStore>;

interface FilterStorageConfig {
  _withStoring?: boolean;
  _storageName?: string;
}

const FILTER_STORAGE_CONFIG = new InjectionToken<FilterStorageConfig>('FilterStorageConfig');

function createStore(): FilterStore {
  return new FilterTableStore();
}

function ensureConfig(storage: string): FilterStorageConfig {
  if (!storage) return { _withStoring: false };
  if (!storage.startsWith('filter')) storage = `filter${storage}`;
  return { _withStoring: true, _storageName: storage };
}
